import React from 'react';
import styled from 'styled-components';
import { AbstractControl, FieldConfig, FormGenerator } from 'react-reactive-form';
import { TFunction } from 'i18next';
import { Row } from 'react-bootstrap';

import TabSelector, { TabSelectorEntity } from './TabSelector';
import { IinstallBase, Ijob, IWorkOrder } from '../../../models';
import { PartsControl } from '../../parts/PartsControl';
import { PhotosControl } from '../../photos/PhotosControl';
import SAPDamageCodes from '../../workOrder/SAP/SAPDamageCodes';
import { PointListButtonBarContainer } from '../PointListButtonBarContainer';
import { WorkOrderLayout } from '../../workOrder/WorkOrderLayout';
import { initialWorkOrder } from '../../../reducers/initialState';
import { DeficiencyChecklist } from '../DeficiencyChecklist';
import { MeasurementPointTabMenu } from '../MeasurementPointTabMenu';
import { TabType } from './tabTypes';

interface IProps {
    installID: string;
    jobID: string;
    handleCannotComplete: () => void;
    shouldHidePhotoTab: boolean;
    shouldHidePartsTab: boolean;
    shouldHideDamageCodesTab: boolean;
    selectedInstall: IinstallBase;
    selectedJob: Ijob;
    selectedWorkOrders: IWorkOrder[];
    formControls: { [key: string]: FieldConfig };
    historicalWorkOrderID: string;
    t: any;
    history: any;
    getWorkOrderTitle: (workOrder: IWorkOrder, t: TFunction) => string;
    handleSubmit: () => void;
    setForm: (form: AbstractControl, id: string | number) => void;
    checklistMode: boolean;
    handleActiveTabSelect: (tabKey: TabType | string | number | any) => void;
    activeTab: TabType | number | string;
    additionalTabs: TabSelectorEntity[];
    visibleTabs: TabSelectorEntity[];
    showAllTabs: boolean;
    onToggleShowAllTabs: () => void;
    isHistoricalMode: boolean;
    hasNotInUseTabs: boolean;
}

interface TabItem {
    name: string;
    Component: any;
    type: TabType;
    onClick?: () => void;
}

const TabSection = styled.div`
    height: 41px;
    display: flex;
    flex-direction: row;
    padding: 0px 0px 0px 16px;
    background: #f1f1f1;
    gap: 4px;
`;

const Tab = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    font-style: normal;
    font-weight: ${(props: any) => (props.isActive ? '800' : '400')};
    font-size: 16px;

    color: ${(props: any) => (props.isActive ? '#013564' : '#007BC3')};
    border-radius: 4px 4px 0px 0px;
    user-select: none;
    background-color: ${(props: any) => (props.isActive ? '#fff' : null)};

    & > span {
        border-bottom: ${(props: any) => (props.isActive ? '1px solid #007BC3' : null)};
        padding: 12px;
        font-size: 16px;
    }

    & > span.mp-tab-back-btn {
        font-size: 24px;
    }

    &:hover {
        background-color: #fff;
        cursor: pointer;
    }
`;

const MainSection = styled.div`
    background-color: #fff;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    padding-bottom: 140px;

    .beacon-form {
        .col-xs-8 {
            padding: 0;
        }
    }

    .beacon-select,
    .ReactTable {
        width: 100%;
    }

    .form-buttons {
        display: flex;
        flex-wrap: wrap;

        > * {
            margin: 0;
        }

        > div {
            padding: 0;
        }

        .col-xs-8 {
            width: 100%;
        }

        .col-xs-2 {
            width: auto;
        }

        .btn {
            height: 43px;
        }
    }
`;

const SectionWrap = styled.div`
    background-color: #fff;
    height: calc(100dvh - 220px);
    overflow: hidden;
`;

export default (props: IProps) => {
    const { handleActiveTabSelect, activeTab, additionalTabs } = props;

    const tabs = React.useMemo(() => {
        const tabsList: TabItem[] = [];

        if (!props.shouldHidePhotoTab) {
            tabsList.push({
                name: 'Photos',
                Component: Tab,
                type: TabType.Photos
            });
        }

        if (!props.shouldHidePartsTab) {
            tabsList.push({
                name: 'Parts',
                Component: Tab,
                type: TabType.Parts
            });
        }

        return tabsList;
    }, [props.shouldHidePhotoTab, props.shouldHidePartsTab]);

    const selectorTabs: TabSelectorEntity[] = React.useMemo(() => {
        if (props.shouldHideDamageCodesTab) {
            return props.visibleTabs;
        }

        const damageCodesTab: TabSelectorEntity = {
            title: 'DamageCodes',
            id: TabType.DamageCodes,
            isWorkOrder: false,
            isMeasurementPoint: false,
            index: -1
        };
        return [damageCodesTab, ...props.visibleTabs];
    }, [props.visibleTabs, props.shouldHideDamageCodesTab]);

    return (
        <>
            <TabSection>
                {tabs.map((tab, index) => {
                    return (
                        <tab.Component
                            key={index}
                            isActive={activeTab === tab.type}
                            onClick={() => {
                                if (tab.onClick) {
                                    tab.onClick();
                                    return;
                                }
                                handleActiveTabSelect(tab.type);
                            }}
                        >
                            <span>{tab.name}</span>
                        </tab.Component>
                    );
                })}
                <TabSelector
                    setActiveTab={handleActiveTabSelect}
                    activeTab={activeTab}
                    tabs={selectorTabs}
                    t={props.t}
                    getWorkOrderTitle={props.getWorkOrderTitle}
                />
                <MeasurementPointTabMenu
                    showAllTabs={props.showAllTabs}
                    onToggleShowAllTabs={props.onToggleShowAllTabs}
                    isHistoricalMode={props.isHistoricalMode}
                    hasNotInUseTabs={props.hasNotInUseTabs}
                    t={props.t}
                />
            </TabSection>
            <SectionWrap>
                <MainSection>
                    {activeTab === TabType.Photos && <PhotosControl installID={props.installID} />}
                    {activeTab === TabType.Parts && (
                        <PartsControl installID={props.installID} history={props.history} />
                    )}
                    {activeTab === TabType.DamageCodes && (
                        <div style={{ paddingLeft: '16px', paddingRight: '16px' }}>
                            <SAPDamageCodes installBase={props.selectedInstall} selectedJob={props.selectedJob} />
                        </div>
                    )}
                    {additionalTabs.map((tab: TabSelectorEntity) => {
                        if (tab?.isMeasurementPoint) {
                            return (
                                <div
                                    key={tab.id}
                                    className="mp-container measurement-point-list-form"
                                    style={{
                                        display: tab.id === activeTab ? undefined : 'none',
                                        paddingLeft: '16px',
                                        paddingRight: '16px'
                                    }}
                                >
                                    {tab.isChecklistTab && (
                                        <Row>
                                            <DeficiencyChecklist
                                                jobID={props.jobID}
                                                installBaseID={props.installID}
                                            />
                                        </Row>
                                    )}
                                    <Row>
                                        <FormGenerator
                                            fieldConfig={props.formControls[tab.id] ?? { controls: {} }}
                                            onMount={form => props.setForm(form, tab.id)}
                                        />
                                    </Row>
                                </div>
                            );
                        }

                        if (tab?.isWorkOrder) {
                            const selectedWorkOrder: IWorkOrder | undefined = props.selectedWorkOrders.find(
                                (workOrder: IWorkOrder) => workOrder.id === tab.id
                            );

                            return (
                                <div
                                    key={tab.id}
                                    style={{
                                        display: selectedWorkOrder?.id === activeTab ? undefined : 'none',
                                        paddingLeft: '1rem',
                                        paddingRight: '1rem'
                                    }}
                                >
                                    <WorkOrderLayout
                                        showChecklist={props.checklistMode}
                                        selectedWorkOrder={selectedWorkOrder ?? initialWorkOrder}
                                        t={props.t}
                                    />
                                </div>
                            );
                        }
                    })}
                </MainSection>
            </SectionWrap>
            <PointListButtonBarContainer
                handleCannotComplete={props.handleCannotComplete}
                installID={props.installID}
                handleSubmit={props.handleSubmit}
            />
        </>
    );
};
